import { useMemo } from "react";

import { compareDistrictNames } from "../../utils/postcodeDistricts";
import { WizardState, formatGbp } from "./types";

interface ReviewStepProps {
  state: WizardState;
  error?: string;
}

export default function ReviewStep({ state, error }: ReviewStepProps) {
  const selected = useMemo(
    () => state.districts.filter((d) => d.selected).sort((a, b) => compareDistrictNames(a.district, b.district)),
    [state.districts]
  );
  const deselected = state.districts.length - selected.length;
  const areas = Array.from(new Set(selected.map((d) => d.area))).sort();
  const nonDefault = selected.filter((d) => d.price !== state.defaultPrice);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Ready to go?</h2>
        <p className="mt-1 text-sm text-gray-600">
          Here's what we'll set up. Everything can be changed later from Postcode Prices, Price Adjustments and Admin
          Settings.
        </p>
      </div>

      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Item label="Yard">
          {state.yardPostcode} · {state.radiusMiles} mile radius
        </Item>
        <Item label="Coverage">
          {selected.length} district{selected.length === 1 ? "" : "s"} across {areas.join(", ") || "—"}
          {deselected > 0 && <span className="block text-xs text-gray-500">{deselected} de-selected</span>}
        </Item>
        <Item label="Collection price">
          {formatGbp(state.defaultPrice)} default
          {nonDefault.length > 0 && (
            <span className="block text-xs text-gray-500">
              {nonDefault.length} district{nonDefault.length === 1 ? "" : "s"} priced individually
            </span>
          )}
        </Item>
        <Item label="Drop-offs">
          {state.doesDropOff
            ? `Accepted${state.dropOffPricePerTonne != null ? ` · ${formatGbp(state.dropOffPricePerTonne)} per tonne` : ""}`
            : "Not accepted"}
        </Item>
        <Item label="SellAVehicle">{state.sellAVehicleQuotesEnabled ? "Enabled (£30 per lead)" : "Off"}</Item>
      </dl>

      {selected.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700">Districts</h3>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {selected.map((d) => (
              <span
                key={d.district}
                className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-700 ring-1 ring-inset ring-primary-600/20"
              >
                {d.district}
                <span className="text-primary-500">{formatGbp(d.price)}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}
    </div>
  );
}

function Item({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{children}</dd>
    </div>
  );
}
